import '../utils/buttons.css';
import React, { useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import { DuplicateItem } from '../types/operations';

interface DuplicateModalViewProps {
    isOpen: boolean;
    onClose: () => void;
    duplicate: DuplicateItem | null;
}

const DuplicateModalView: React.FC<DuplicateModalViewProps> = React.memo(({ isOpen, onClose, duplicate }) => {
    const formatCurrency = useCallback((value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    }, []);

    const formatDate = useCallback((dateString: string) => {
        if (!dateString) return '-';
        // Extrair YYYY-MM-DD e converter para DD/MM/YYYY sem timezone
        const dateOnly = dateString.split('T')[0];
        const [year, month, day] = dateOnly.split('-');
        return `${day}/${month}/${year}`;
    }, []);

    // Early return para performance
    if (!isOpen || !duplicate) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Detalhes da Duplicata
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {/* Informações do Documento */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Documento</h3>
                            <div className="space-y-2">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">ID do Documento</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.document_id || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Título</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.document_title || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CFOP</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.cfop || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Data de Emissão</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{formatDate(duplicate.issue_date)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Informações do Cedente */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Cedente</h3>
                            <div className="space-y-2">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.assignor_name || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CNPJ</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.assignor_cnpj || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Informações do Devedor */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Devedor</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.debtor_name || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CPF/CNPJ</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.debtor_cpf_cnpj || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.debtor_email || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Telefone</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.address_phone || '-'}</p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Endereço</label>
                                    <p className="text-sm text-gray-900 dark:text-white">
                                        {[
                                            duplicate.address_street,
                                            duplicate.address_number,
                                            duplicate.address_complement
                                        ].filter(Boolean).join(', ') || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Bairro</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.address_district || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cidade/Estado</label>
                                    <p className="text-sm text-gray-900 dark:text-white">
                                        {[duplicate.address_city, duplicate.address_state].filter(Boolean).join('/') || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CEP</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.address_zip_code || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Código IBGE</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.address_ibge_code || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Informações do Recebível */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recebível</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Número</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.receivable_number || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Data de Vencimento</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{formatDate(duplicate.receivable_due_date)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Valor</label>
                                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                                        {formatCurrency(duplicate.receivable_amount)}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Valor do Desembolso</label>
                                    <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                                        {formatCurrency(duplicate.disbursement_value || 0)}
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">V. Pag</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.receivable_v_pag || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Natureza da Operação</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.receivable_nat_op || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Indicador de Pagamento</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{duplicate.receivable_ind_pag || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">% Aval da Operação</label>
                                    <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                        {duplicate.guarantee_percent ? `${duplicate.guarantee_percent.toFixed(2)}%` : '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Informações do Indexador */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Indexador</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome</label>
                                <p className="text-sm text-gray-900 dark:text-white">{duplicate.fee_name || '-'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Valor</label>
                                <p className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                                    {duplicate.fee_value ? `${duplicate.fee_value.toFixed(2)}%` : '-'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Informações Adicionais */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Informações Adicionais</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Risco Principal</label>
                                <p className="text-sm text-gray-900 dark:text-white">{duplicate.main_risk || '-'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Card do JIRA</label>
                                <p className="text-sm text-gray-900 dark:text-white">{duplicate.external_link || '-'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="text-white bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
});

DuplicateModalView.displayName = 'DuplicateModalView';

export default DuplicateModalView;